import styled, { css, keyframes } from 'styled-components';
import { MoreStrip } from './PremadeRedesign.styles';

/* cards appended by »učitaj još« fade up softly; the per-card stagger comes
   from an inline animation-delay, so backwards keeps them hidden until then */
const loadFade = keyframes`
	from {
		opacity: 0;
		transform: translateY(10px);
	}
`;

const loadFadeIn = css`
	&.fade-new {
		animation: ${loadFade} 0.35s ease backwards;
	}

	@media (prefers-reduced-motion: reduce) {
		&.fade-new {
			animation: none;
		}
	}
`;

export const ProfilePage = styled.div`
	text-align: left;

	/* tab switches remount the outlet wrapper: content fades up softly */
	.tab-fade {
		animation: pr-tab 0.35s ease;
	}

	@keyframes pr-tab {
		from {
			opacity: 0;
			transform: translateY(10px);
		}
	}

	@media (prefers-reduced-motion: reduce) {
		.tab-fade {
			animation: none;
		}
	}
`;

export const PrHead = styled.div`
	padding: 150px var(--pad) 20px;
	display: flex;
	align-items: flex-end;
	justify-content: space-between;
	gap: 24px;
	flex-wrap: wrap;

	/* signature: solid coral nickname on its own row above the avatar+title;
	   font-size is fitted to the header width by Profile.tsx */
	.wm {
		flex: 0 0 100%;
		font-family: var(--fh);
		line-height: 1;
		white-space: nowrap;
		color: var(--coral);
		transform: rotate(-2deg);
		/* opacity-only entrance so it can't fight the fitted font-size or
		   the rotation transform */
		animation: pr-wm 0.9s ease 0.1s backwards;
	}

	@keyframes pr-wm {
		from {
			opacity: 0;
		}
	}

	@media (prefers-reduced-motion: reduce) {
		.wm {
			animation: none;
		}
	}

	/* skeleton stand-ins while profileInfo loads, sized like what they
	   replace so the header doesn't jump when the real content lands */
	.wm-sk {
		flex: 0 0 100%;
		height: clamp(40px, 7vw, 130px);
		max-width: min(560px, 62%);
		transform: rotate(-2deg);
		transform-origin: left;
	}

	.av-sk {
		width: 100%;
		height: 100%;
		border-radius: 50%;
	}

	.me {
		display: flex;
		align-items: flex-end;
		gap: clamp(18px, 2.4vw, 30px);
	}

	.big-av {
		position: relative;
		width: clamp(76px, 7vw, 104px);
		height: clamp(76px, 7vw, 104px);
		border-radius: 50%;
		overflow: hidden;
		flex-shrink: 0;
		cursor: pointer;
		border: none;
		padding: 0;
		background: var(--panel);
	}

	.big-av img {
		width: 100%;
		height: 100%;
		object-fit: cover;
		display: block;
	}

	.big-av .edit {
		position: absolute;
		inset: 0;
		display: flex;
		align-items: center;
		justify-content: center;
		background: rgba(24, 14, 2, 0.45);
		color: #fbf5ea;
		opacity: 0;
		transition: opacity 0.2s;
		font-size: 12px;
		font-weight: 700;
		font-family: var(--fb);
	}

	.big-av:hover .edit {
		opacity: 1;
	}

	h1 {
		/* browser default h1 margins (0.67em) would break the flex-end
		   alignment of avatar and buttons against the title */
		margin: 0;
		font-size: clamp(44px, 6.4vw, 92px);
		line-height: 0.94;
	}

	.acts {
		display: flex;
		align-items: center;
		gap: 10px;
	}

	.acts button {
		display: inline-flex;
		align-items: center;
		gap: 8px;
		font-family: var(--fb);
		font-size: 14px;
		font-weight: 600;
		text-transform: lowercase;
		color: var(--brown);
		background: none;
		border: 1.5px solid rgba(var(--ink-rgb), 0.3);
		border-radius: var(--r-pill);
		padding: 9px 16px;
		cursor: pointer;
		transition: 0.2s;
	}

	.acts button:hover {
		border-color: var(--ink);
		color: var(--ink);
	}

	/* mobile (M10 pick): settings/logout live in the bottom-nav »više«
	   sheet on touch widths — the header keeps only signature, avatar and
	   title. Desktop keeps the pills. */
	@media (max-width: 1079px) {
		.acts {
			display: none;
		}
	}
`;

export const ProfileTabs = styled.div`
	display: flex;
	gap: 24px;
	padding: 26px var(--pad) 6px;
	flex-wrap: wrap;
	align-items: baseline;

	button {
		font-family: var(--fd);
		font-size: clamp(17px, 1.6vw, 23px);
		letter-spacing: 0.4px;
		background: none;
		border: none;
		color: var(--mut);
		padding: 0 0 4px;
		border-bottom: 2px solid transparent;
		transition: 0.2s;
		cursor: pointer;
		text-transform: uppercase;
	}

	button:hover {
		color: var(--ink);
	}

	button.on {
		color: var(--teal-dark);
		border-color: var(--teal-dark);
	}

	/* on a phone the tabs read as headings at full size — shrink them to the
	   TagBar's mobile tag size so they sit as a filter rail, not titles */
	@media (max-width: 640px) {
		row-gap: 10px;

		button {
			font-size: 14px;
		}
	}
`;

/** Owned-quiz rows. */
export const Own = styled.div`
	border-top: 1px solid var(--hair);
	margin-top: 24px;

	/* the shared load-more strip keeps /premade's big 130px page-end padding;
	   here the gratis panel follows, so bottom matches the 70px top */
	${MoreStrip} {
		padding-bottom: 70px;
	}
`;

/* /profile/quizzes shelf, proposal 11 »grande panorama« card language.
   Grid mode (and every viewport below the desktop breakpoint) uses the
   upright card: untouched 3:2 cover on top, title on paper below it, a
   horizontal perforation strip and a 64px coupon. Fluid column count like
   TxGrid (4 on wide desktops, 3/2 as the viewport shrinks), 1 on mobile. */
export const PanGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
	gap: clamp(18px, 2.6vw, 30px);
	align-items: start;
	padding: 30px var(--pad) 0;

	@media (max-width: 720px) {
		grid-template-columns: 1fr;
	}

	.pan-card-sk {
		aspect-ratio: 4 / 5;
		border-radius: 18px;
	}
`;

export const PanCard = styled.div`
	${loadFadeIn}
	position: relative;
	border-radius: 18px;
	overflow: hidden;
	background: var(--panel-2);
	cursor: pointer;
	box-shadow: ${({ theme }) =>
		theme.mode === 'dark'
			? '0 16px 34px rgba(0, 0, 0, 0.45)'
			: '0 16px 34px rgba(var(--ink-rgb), 0.12)'};
	transition:
		transform 0.25s ease,
		box-shadow 0.25s ease;
	/* real punched holes on the horizontal perforation (64px coupon + half
	   of the 18px hole strip) — the page shows through the hole */
	--sh: 73px;
	-webkit-mask-image: radial-gradient(
			circle 10px at 0 calc(100% - var(--sh)),
			transparent 10px,
			#000 10.5px
		),
		radial-gradient(
			circle 10px at 100% calc(100% - var(--sh)),
			transparent 10px,
			#000 10.5px
		);
	-webkit-mask-composite: source-in;
	mask-image: radial-gradient(
			circle 10px at 0 calc(100% - var(--sh)),
			transparent 10px,
			#000 10.5px
		),
		radial-gradient(
			circle 10px at 100% calc(100% - var(--sh)),
			transparent 10px,
			#000 10.5px
		);
	mask-composite: intersect;

	&:hover {
		transform: translateY(-4px);
	}

	/* cover keeps its native 3:2 — never cropped; darkzone only under
	   the signature */
	.ph {
		position: relative;
		aspect-ratio: 3 / 2;
		overflow: hidden;
		background: #17130f;
	}

	.ph img.bg {
		position: absolute;
		inset: 0;
		width: 100%;
		height: 100%;
		object-fit: cover;
		transition: transform 0.5s;
	}

	&:hover .ph img.bg {
		transform: scale(1.04);
	}

	.ph::after {
		content: '';
		position: absolute;
		inset: 0 0 55% 0;
		background: linear-gradient(
			180deg,
			rgba(10, 8, 6, 0.45),
			rgba(10, 8, 6, 0)
		);
	}

	/* author signature in brand color: quizzach wordmark, or Q ✕ author
	   logo (pre-tinted neon PNG) / handwritten name */
	.amark {
		position: absolute;
		top: 14px;
		left: 16px;
		z-index: 1;
		display: inline-flex;
		align-items: center;
		gap: 7px;
		color: var(--teal);
		pointer-events: none;
	}

	.amark svg {
		display: block;
		flex-shrink: 0;
	}

	.amark .author-img img {
		display: block;
		height: 22px;
		width: auto;
	}

	.amark .author-img .v-base {
		display: none;
	}

	.amark .author-img .v-neon {
		display: block;
	}

	.amark .author-hand {
		font-family: var(--fh);
		font-weight: 600;
		font-size: 23px;
		line-height: 1;
		color: var(--teal);
		white-space: nowrap;
	}

	/* handwritten »novo!« in the photo's top right corner */
	.fresh-note {
		position: absolute;
		top: 12px;
		right: 16px;
		z-index: 1;
		font-family: var(--fh);
		font-weight: 600;
		font-size: 22px;
		line-height: 1;
		color: var(--gold);
		transform: rotate(2deg);
		text-shadow: 0 1px 10px rgba(0, 0, 0, 0.55);
		pointer-events: none;
	}

	/* title on paper in theme colors — no scrim fight with the cover */
	.tt {
		padding: 14px 18px 16px;
	}

	.tt h3 {
		font-size: 21px;
		line-height: 1.12;
	}

	.tt .im {
		display: block;
		margin-top: 6px;
		font-size: 10px;
		font-weight: 700;
		letter-spacing: 1.8px;
		text-transform: uppercase;
		color: var(--mut);
	}

	/* perforation: page-colored dots on the paper strip */
	.holes {
		height: 18px;
		background-color: var(--panel-2);
		background-image: radial-gradient(
			circle 4px at 8px 9px,
			var(--cream) 4px,
			transparent 4.5px
		);
		background-size: 16px 18px;
		background-repeat: repeat-x;
	}

	/* paper coupon: security lines, barcode, pokreni */
	.stub {
		height: 64px;
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 14px;
		padding: 0 18px;
		cursor: default;
		background-image: repeating-linear-gradient(
			45deg,
			rgba(var(--ink-rgb), 0.025) 0 1px,
			transparent 1px 8px
		);
	}

	/* the code text sizes the column; the bars stretch to match it */
	.stub .bc {
		display: flex;
		flex-direction: column;
		gap: 4px;
		min-width: 0;
		overflow: hidden;
	}

	/* barcode: two layered repeating-gradients, no asset */
	.stub .bars {
		height: 24px;
		color: var(--ink);
		background: repeating-linear-gradient(
				90deg,
				currentColor 0 2px,
				transparent 2px 5px,
				currentColor 5px 6px,
				transparent 6px 11px
			),
			repeating-linear-gradient(90deg, currentColor 0 1px, transparent 1px 7px);
	}

	.stub .code {
		font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace;
		font-size: 8.5px;
		letter-spacing: 2.2px;
		text-transform: uppercase;
		color: var(--mut);
		white-space: nowrap;
	}

	.stub button {
		flex-shrink: 0;
	}
`;

/* grid/list switch above the shelf — desktop only, the list layout needs
   room. Soft-tint pills in the payment-tile language: quiet ink wash at
   rest, teal wash + teal-dark glyph when active. */
export const ViewBar = styled.div`
	display: flex;
	justify-content: flex-end;
	padding: 26px var(--pad) 0;

	.views {
		display: inline-flex;
		gap: 6px;
	}

	button {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 38px;
		height: 38px;
		border: none;
		border-radius: 999px;
		background: rgba(var(--ink-rgb), 0.06);
		color: var(--mut);
		cursor: pointer;
		transition:
			background 0.2s,
			color 0.2s;
	}

	button:hover {
		color: var(--ink);
	}

	button[aria-pressed='true'] {
		background: ${({ theme }) =>
			theme.mode === 'dark'
				? 'rgba(0, 255, 194, 0.14)'
				: 'rgba(0, 128, 97, 0.12)'};
		color: var(--teal-dark);
		cursor: default;
	}

	@media (max-width: 1079px) {
		display: none;
	}
`;

/* /profile/quizzes list mode: proposal 11 »grande panorama« — full-width
   ticket rows with the 08 anatomy (hole strip, paper coupon, serial rail),
   but the cover keeps its own untouched landscape column and the title +
   gold-labeled fields sit on paper in theme colors. Desktop only; below
   1080px the view falls back to the PanCard grid. */
export const PanList = styled.div`
	display: grid;
	grid-template-columns: 1fr;
	gap: clamp(18px, 2.6vw, 26px);
	padding: 30px var(--pad) 0;

	.pan-sk {
		height: 267px;
		border-radius: 18px;
	}
`;

export const Pan = styled.div`
	${loadFadeIn}
	position: relative;
	display: grid;
	/* DOM order = photo / paper / holes / stub / rail; covers upload at 3:2,
	   so photo column max 400px × the 267px min-height shows them uncropped
	   until the paper side grows taller */
	grid-template-columns: minmax(280px, 400px) minmax(0, 1fr) 18px 240px 44px;
	min-height: 267px;
	border-radius: 18px;
	overflow: hidden;
	background: var(--panel-2);
	cursor: pointer;
	box-shadow: ${({ theme }) =>
		theme.mode === 'dark'
			? '0 16px 34px rgba(0, 0, 0, 0.45)'
			: '0 16px 34px rgba(var(--ink-rgb), 0.12)'};
	transition:
		transform 0.25s ease,
		box-shadow 0.25s ease;
	/* big punched holes at the perforation ends (through the card):
	   the perforation sits 293px from the right edge (44 rail + 240 stub + 9) */
	-webkit-mask-image: radial-gradient(
			circle 11px at calc(100% - 293px) 0,
			transparent 11px,
			#000 11.5px
		),
		radial-gradient(
			circle 11px at calc(100% - 293px) 100%,
			transparent 11px,
			#000 11.5px
		);
	-webkit-mask-composite: source-in;
	mask-image: radial-gradient(
			circle 11px at calc(100% - 293px) 0,
			transparent 11px,
			#000 11.5px
		),
		radial-gradient(
			circle 11px at calc(100% - 293px) 100%,
			transparent 11px,
			#000 11.5px
		);
	mask-composite: intersect;

	&:hover {
		transform: translateY(-3px);
	}

	/* photo column: full untouched cover, darkzone only under the signature */
	.ph {
		position: relative;
		overflow: hidden;
		background: #17130f;
	}

	.ph img.bg {
		position: absolute;
		inset: 0;
		width: 100%;
		height: 100%;
		object-fit: cover;
		transition: transform 0.5s;
	}

	&:hover .ph img.bg {
		transform: scale(1.04);
	}

	.ph::after {
		content: '';
		position: absolute;
		inset: 0 0 60% 0;
		background: linear-gradient(
			180deg,
			rgba(10, 8, 6, 0.42),
			rgba(10, 8, 6, 0)
		);
	}

	.amark {
		position: absolute;
		top: 14px;
		left: 16px;
		z-index: 1;
		display: inline-flex;
		align-items: center;
		gap: 7px;
		color: var(--teal);
		pointer-events: none;
	}

	.amark svg {
		display: block;
		flex-shrink: 0;
	}

	.amark .author-img img {
		display: block;
		height: 22px;
		width: auto;
	}

	.amark .author-img .v-base {
		display: none;
	}

	.amark .author-img .v-neon {
		display: block;
	}

	.amark .author-hand {
		font-family: var(--fh);
		font-weight: 600;
		font-size: 23px;
		line-height: 1;
		color: var(--teal);
		white-space: nowrap;
	}

	/* paper pane: title and fields in theme colors — readability no longer
	   depends on a scrim over the cover */
	.in {
		display: flex;
		flex-direction: column;
		justify-content: space-between;
		min-width: 0;
		padding: 20px 30px;
	}

	.carrier {
		display: flex;
		justify-content: space-between;
		gap: 14px;
		font-size: 9.5px;
		font-weight: 700;
		letter-spacing: 2.6px;
		text-transform: uppercase;
		color: var(--mut);
	}

	.fresh-note {
		display: block;
		width: max-content;
		font-family: var(--fh);
		font-weight: 600;
		font-size: 24px;
		line-height: 1;
		color: var(--gold-ink);
		transform: rotate(-2deg);
		margin: 8px 0 0;
	}

	h3 {
		font-size: clamp(24px, 2.4vw, 38px);
		margin-top: 4px;
	}

	.fields {
		display: flex;
		flex-wrap: wrap;
		gap: 12px clamp(20px, 2.6vw, 44px);
		margin-top: 18px;
	}

	.f .lb {
		display: block;
		font-size: 9.5px;
		font-weight: 700;
		letter-spacing: 2px;
		text-transform: uppercase;
		color: var(--gold-ink);
	}

	.f .val {
		display: block;
		font-family: var(--fd);
		font-size: 17px;
		text-transform: uppercase;
		color: var(--ink);
		margin-top: 3px;
		white-space: nowrap;
	}

	/* perforation: page-colored dots on the paper strip */
	.holes {
		background-color: var(--panel-2);
		background-image: radial-gradient(
			circle 4px at 9px 8px,
			var(--cream) 4px,
			transparent 4.5px
		);
		background-size: 18px 16px;
		background-repeat: repeat-y;
	}

	/* paper coupon: security lines, barcode, pokreni */
	.stub {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: 16px;
		padding: 18px;
		cursor: default;
		background-image: repeating-linear-gradient(
			45deg,
			rgba(var(--ink-rgb), 0.025) 0 1px,
			transparent 1px 8px
		);
	}

	/* the code text sizes the column; the bars stretch to match it */
	.stub .bc {
		display: flex;
		flex-direction: column;
		gap: 6px;
	}

	.stub .bars {
		height: 30px;
		color: var(--ink);
		background: repeating-linear-gradient(
				90deg,
				currentColor 0 2px,
				transparent 2px 5px,
				currentColor 5px 6px,
				transparent 6px 11px
			),
			repeating-linear-gradient(90deg, currentColor 0 1px, transparent 1px 7px);
	}

	.stub .code {
		font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace;
		font-size: 9px;
		letter-spacing: 2.2px;
		text-transform: uppercase;
		color: var(--mut);
		white-space: nowrap;
	}

	/* serial rail: the vertical text lives in an absolute span so its run
	   length can never set the grid row's intrinsic height */
	.rail {
		position: relative;
		border-left: 1px solid rgba(var(--ink-rgb), 0.14);
	}

	.rail span {
		position: absolute;
		inset: 0;
		writing-mode: vertical-rl;
		transform: rotate(180deg);
		display: flex;
		align-items: center;
		justify-content: center;
		font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace;
		font-size: 9px;
		letter-spacing: 2.4px;
		text-transform: uppercase;
		color: rgba(var(--ink-rgb), 0.38);
		padding: 12px 0;
		white-space: nowrap;
		overflow: hidden;
	}
`;

/** Gratis mini-quiz ruled panel with the dashed code box. */
export const Gratis = styled.div`
	background: var(--paper);
	margin: 90px var(--gut) 0;
	border-radius: var(--r);
	/* content aligns with the page's --pad column while the panel stays a
	   --gut band, same as the premade hero plate */
	padding: 70px max(clamp(22px, 4vw, 60px), calc(var(--pad) - var(--gut)));
	position: relative;
	overflow: hidden;
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 34px;
	flex-wrap: wrap;

	&::before {
		content: '';
		position: absolute;
		inset: 0;
		opacity: ${({ theme }) => (theme.mode === 'dark' ? 0.9 : 0.55)};
		pointer-events: none;
		background: repeating-linear-gradient(
			180deg,
			transparent 0 44px,
			rgba(var(--ink-rgb), 0.045) 44px 45px
		);
	}

	h2 {
		font-size: clamp(30px, 3.6vw, 52px);
		position: relative;
	}

	.hand {
		font-family: var(--fh);
		font-weight: 600;
		color: var(--coral);
		font-size: clamp(22px, 2.2vw, 30px);
		display: block;
		transform: rotate(-2deg);
	}

	p {
		color: var(--brown);
		max-width: 440px;
		margin-top: 10px;
		position: relative;
	}

	.gratis-side {
		position: relative;
		display: flex;
		flex-direction: column;
		gap: 16px;
		align-items: flex-start;
	}

	/* CTA under the code box: pushed down a touch and flushed right */
	.gratis-side > button + button {
		align-self: flex-end;
		margin-top: 6px;
	}

	/* CTA-only variant: stacked buttons share one size regardless of label
	   length or border differences between variants. */
	.gratis-side.stack > button {
		min-width: 250px;
		min-height: 47px;
	}

	@media (max-width: 1079px) {
		.gratis-side.stack {
			width: 100%;
		}

		.gratis-side.stack > button {
			width: 100%;
		}
	}
`;

export const CodeBox = styled.button`
	position: relative;
	display: flex;
	align-items: center;
	gap: 18px;
	border: 2px dashed rgba(var(--ink-rgb), 0.35);
	border-radius: var(--r);
	padding: 18px 28px;
	background: ${({ theme }) =>
		theme.mode === 'dark' ? 'rgba(29,31,33,0.7)' : 'rgba(255,249,238,0.7)'};
	cursor: pointer;

	.c {
		font-family: var(--fd);
		font-size: clamp(18px, 2.4vw, 28px);
		letter-spacing: 3px;
		color: var(--ink);
		text-transform: uppercase;
	}

	.ic {
		color: var(--mut);
		display: flex;
		transition: color 0.2s;
	}

	&:hover .ic {
		color: var(--teal-dark);
	}
`;

// ------------------------------------------------------------------ //
// ProfileAvatar (avatar picker page).
// ------------------------------------------------------------------ //

export const AvPage = styled.div`
	text-align: left;
	min-height: 100vh;
`;

export const AvHead = styled.div`
	padding: 150px var(--pad) 30px;
	display: flex;
	align-items: flex-end;
	justify-content: space-between;
	gap: 24px;
	flex-wrap: wrap;

	h1 {
		font-size: clamp(38px, 5.4vw, 76px);
		line-height: 0.94;
	}

	.x {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 44px;
		height: 44px;
		border-radius: 50%;
		border: 1.5px solid rgba(var(--ink-rgb), 0.3);
		background: none;
		color: var(--brown);
		cursor: pointer;
		transition: 0.2s;
	}

	.x:hover {
		border-color: var(--ink);
		color: var(--ink);
	}
`;

export const AvGrid = styled.div`
	padding: 10px var(--pad) 0;
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
	gap: clamp(18px, 2.6vw, 32px);
`;

export const AvItem = styled.button<{ $on?: boolean }>`
	position: relative;
	aspect-ratio: 1;
	border-radius: 50%;
	overflow: hidden;
	padding: 0;
	border: none;
	background: var(--panel);
	cursor: pointer;
	outline: 3px solid ${({ $on }) => ($on ? 'var(--teal)' : 'transparent')};
	outline-offset: 4px;
	transition:
		transform 0.2s,
		outline-color 0.2s;

	&:hover {
		transform: scale(1.05);
	}

	img {
		width: 100%;
		height: 100%;
		object-fit: cover;
		display: block;
	}
`;

export const AvPlaceholder = styled.div`
	aspect-ratio: 1;
	border-radius: 50%;
	background: var(--panel);
`;

export const AvActions = styled.div`
	padding: 40px var(--pad) 120px;
`;

// ------------------------------------------------------------------ //
// UserAuthorAnalytics (author sales tab).
// ------------------------------------------------------------------ //

export const AnHead = styled.div`
	padding: 24px var(--pad);
	display: flex;
	align-items: center;
	justify-content: space-between;
	flex-wrap: wrap;
	gap: 18px 28px;
	border-bottom: 1px solid var(--hair);

	.disp {
		font-family: var(--fh);
		font-weight: 600;
		font-size: clamp(26px, 2.8vw, 38px);
		color: var(--logo);
		display: inline-block;
		transform: rotate(-1.5deg);
		line-height: 1.2;
	}

	.disp.ghost {
		color: transparent;
	}

	.logo-quizzach {
		color: var(--logo);
		display: inline-flex;
	}

	.logo-nogac {
		width: clamp(70px, 7vw, 90px);
		display: block;
	}

	.logo-vragolasti {
		width: clamp(120px, 12vw, 180px);
		display: block;
	}

	/* phones: name/logo centered on its own row above the stats */
	@media (max-width: 720px) {
		justify-content: center;
	}
`;

export const AnStats = styled.div`
	display: flex;
	justify-content: flex-end;
	margin-left: auto;

	.cell {
		padding: 0 clamp(16px, 2.5vw, 36px);
		text-align: center;
	}

	.cell:last-child {
		padding-right: 0;
	}

	.cell + .cell {
		border-left: 1px solid var(--hair);
	}

	.n {
		display: block;
		font-family: var(--fd);
		font-size: clamp(26px, 2.6vw, 36px);
		line-height: 1;
		color: var(--coral);
	}

	.n.ghost {
		color: transparent;
	}

	.l {
		display: block;
		margin-top: 6px;
		font-size: 11.5px;
		font-weight: 700;
		letter-spacing: 1.4px;
		text-transform: uppercase;
		color: var(--mut);
		max-width: 220px;
		white-space: pre-line;
	}

	/* phones: full row centered under the name, the number carries the
	   cell and the label steps back */
	@media (max-width: 720px) {
		width: 100%;
		margin-left: 0;
		justify-content: center;

		.cell,
		.cell:last-child {
			flex: 1;
			padding: 0 8px;
		}

		.n {
			font-size: 38px;
		}

		.l {
			margin-top: 8px;
			font-size: 9px;
			letter-spacing: 1.1px;
			max-width: 150px;
		}
	}
`;

export const AnTabs = styled.div`
	display: flex;
	justify-content: center;
	gap: 28px;
	padding: 36px var(--pad) 6px;
	flex-wrap: wrap;

	button {
		font-family: var(--fd);
		font-size: 15px;
		letter-spacing: 0.4px;
		text-transform: uppercase;
		background: none;
		border: none;
		color: var(--mut);
		padding: 0 0 4px;
		border-bottom: 2px solid transparent;
		cursor: pointer;
		transition: 0.2s;
	}

	button:hover {
		color: var(--ink);
	}

	button.on {
		color: var(--teal-dark);
		border-color: var(--teal-dark);
	}
`;

export const AnCardGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
	gap: clamp(18px, 2.6vw, 30px);
	align-items: start;
	padding: 24px var(--pad) 10px;

	@media (max-width: 720px) {
		grid-template-columns: 1fr;
	}

	.an-card-sk {
		aspect-ratio: 3 / 4;
		border-radius: 14px;
	}
`;

/** Author-analytics stat card: collection cover on top (glass play/open
    buttons on the photo), receipt tally below — the sold count is the
    card's only loud number, closed by the receipt's sawtooth edge. */
export const AnCard = styled.article`
	/* dark needs a true black shadow — the light-ink glow smears the zig teeth */
	filter: ${({ theme }) =>
		theme.mode === 'dark'
			? 'drop-shadow(0 14px 22px rgba(0, 0, 0, 0.4))'
			: 'drop-shadow(0 14px 22px rgba(27, 20, 14, 0.09))'};

	.ph {
		position: relative;
		aspect-ratio: 3 / 2;
		border-radius: 14px 14px 0 0;
		overflow: hidden;
		background: #17130f;
	}

	.ph img {
		width: 100%;
		height: 100%;
		object-fit: cover;
		display: block;
	}

	/* soft bottom scrim keeps the glass buttons legible on any photo */
	.ph::before {
		content: '';
		position: absolute;
		inset: 62% 0 0 0;
		z-index: 1;
		background: linear-gradient(0deg, rgba(10, 8, 6, 0.42), rgba(10, 8, 6, 0));
	}

	.acts {
		position: absolute;
		right: 10px;
		bottom: 10px;
		z-index: 2;
		display: flex;
		gap: 8px;
		align-items: center;
	}

	.ic {
		width: 42px;
		height: 42px;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		padding: 0;
		border: 1px solid rgba(255, 255, 255, 0.3);
		border-radius: 999px;
		background: rgba(15, 12, 9, 0.45);
		backdrop-filter: blur(5px);
		-webkit-backdrop-filter: blur(5px);
		color: #fff;
		cursor: pointer;
		transition: transform 0.2s;
	}

	.ic:hover {
		transform: translateY(-2px);
	}

	.ic svg {
		display: block;
	}

	.bod {
		background: var(--panel-2);
		padding: 15px 18px 4px;
	}

	.bod h3 {
		margin: 0;
		font-size: 19px;
		padding-bottom: 12px;
	}

	.tot {
		border-top: 1px solid rgba(var(--ink-rgb), 0.25);
		padding: 10px 0 12px;
	}

	.tot .n {
		display: block;
		font-family: var(--fd);
		font-size: 40px;
		line-height: 1;
		color: var(--coral);
	}

	.tot .n.zero {
		color: var(--mut);
	}

	.tot .l {
		display: block;
		margin-top: 5px;
		font-size: 11.5px;
		font-weight: 700;
		letter-spacing: 1.8px;
		text-transform: uppercase;
		color: var(--mut);
	}

	/* nazubljeni donji rub s računa (TxTicket .zig) */
	.zig {
		height: 12px;
		background:
			linear-gradient(-45deg, transparent 8.49px, var(--panel-2) 0) 0 0 / 24px
				12px repeat-x,
			linear-gradient(45deg, transparent 8.49px, var(--panel-2) 0) 12px 0 / 24px
				12px repeat-x;
	}
`;

export const AnHRows = styled.div`
	border-top: 1px solid var(--hair);
	margin-top: 18px;
`;

export const AnHRow = styled.div`
	display: flex;
	align-items: center;
	gap: clamp(14px, 3vw, 34px);
	padding: 12px var(--pad);
	border-bottom: 1px solid var(--hair);
	transition: background 0.25s;

	&:hover {
		background: rgba(var(--ink-rgb), 0.03);
	}

	/* sold copy's cover, so sales are recognizable at a glance */
	.th {
		width: 56px;
		aspect-ratio: 3 / 2;
		border-radius: 8px;
		overflow: hidden;
		flex-shrink: 0;
		background: var(--panel);
	}

	.th img {
		width: 100%;
		height: 100%;
		object-fit: cover;
		display: block;
	}

	.when {
		font-size: 13px;
		font-weight: 700;
		letter-spacing: 1.6px;
		text-transform: uppercase;
		color: var(--mut);
		white-space: nowrap;
	}

	.title {
		font-family: var(--fd);
		font-size: clamp(16px, 1.7vw, 21px);
		color: var(--ink);
		text-transform: uppercase;
		letter-spacing: 0.3px;
	}
`;

export const AnEmpty = styled.p`
	padding: 26px var(--pad);
	color: var(--mut);
	font-size: 15.5px;
	border-bottom: 1px solid var(--hair);
`;

// ------------------------------------------------------------------ //
// UserSettings.
// ------------------------------------------------------------------ //

export const SetSec = styled.section`
	padding: 26px var(--pad) 0;
	text-align: left;

	.rows {
		border-top: 1px solid var(--hair);
		border-bottom: 1px solid var(--hair);
		margin-top: 14px;
	}

	.row {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 16px;
		padding: 22px 20px;
		cursor: pointer;
		transition: background 0.25s;

		@media (max-width: 720px) {
			padding: 22px 0;
		}
	}

	.row:hover {
		background: rgba(var(--ink-rgb), 0.03);
	}

	.row.editing {
		cursor: default;
		padding-top: 14px;
		padding-bottom: 14px;

		@media (max-width: 720px) {
			flex-wrap: wrap;
			gap: 12px;
		}
	}

	.row.editing:hover {
		background: none;
	}

	.row .lbl {
		font-size: 16.5px;
		color: var(--ink);
		font-weight: 500;
	}

	.row .edit-ic {
		display: inline-flex;
		color: var(--teal-dark);
	}

	.row .edit-ic svg path {
		stroke: currentColor;
	}

	.x {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		flex-shrink: 0;
		width: 36px;
		height: 36px;
		border-radius: 50%;
		border: 1.5px solid rgba(var(--ink-rgb), 0.3);
		background: none;
		color: var(--brown);
		cursor: pointer;
		transition: 0.2s;
	}

	.x .x-ic {
		display: inline-flex;
	}

	.x .x-txt {
		display: none;
	}

	@media (max-width: 720px) {
		.x {
			order: 1;
			flex: 1 1 0;
			width: auto;
			height: auto;
			border-radius: var(--r-pill);
			padding: 11px 22px;
			font-family: var(--fd);
			font-size: 14px;
			letter-spacing: 0.4px;
			text-transform: lowercase;
		}

		.x .x-ic {
			display: none;
		}

		.x .x-txt {
			display: inline;
		}

		.save {
			order: 2;
			flex: 1 1 0;
		}
	}

	.x:hover {
		border-color: var(--ink);
		color: var(--ink);
	}

	.in-row {
		display: flex;
		align-items: center;
		gap: 12px;
		min-width: 0;

		@media (max-width: 720px) {
			flex: 1 1 100%;
			flex-wrap: wrap;
			justify-content: flex-end;
		}
	}

	input {
		width: 350px;
		flex-shrink: 1;
		min-width: 0;

		@media (max-width: 720px) {
			width: auto;
			flex: 1 1 100%;
		}
		background: var(--panel-2);
		border: 1.5px solid rgba(var(--ink-rgb), 0.3);
		border-radius: var(--r-pill);
		padding: 9px 18px;
		font-family: var(--fb);
		font-size: 15.5px;
		color: var(--ink);
		outline: none;
		transition: border-color 0.2s;
	}

	input:focus {
		border-color: var(--teal-dark);
	}

	input::placeholder {
		color: var(--mut);
	}

	.msgs {
		padding: 0 20px 16px;

		@media (max-width: 720px) {
			padding: 0 0 16px;
		}
	}
`;

// ------------------------------------------------------------------ //
// UserTransactions (order history) — »račun iz birtije«: every order is
// a cash-register receipt with a zigzag torn bottom edge. Theme-aware
// (founder picked the darkish receipt over the paper-stays-light variant);
// header and total rules are solid, only the price leaders stay dotted.
// ------------------------------------------------------------------ //

export const TxGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
	gap: clamp(18px, 2.6vw, 30px);
	align-items: start;
	padding: 30px var(--pad) 20px;
`;

export const TxEmpty = styled.p`
	padding: 26px var(--pad);
	color: var(--mut);
	font-size: 15.5px;
`;

/** Transactions' load-more strip: same 70px bottom as the quizzes tab (Own's
    override) so the gratis panel below sits at the same distance in both tabs. */
export const TxMore = styled(MoreStrip)`
	padding-bottom: 70px;
`;

export const TxTicket = styled.article`
	${loadFadeIn}
	/* dark needs a true black shadow — the light-ink glow smears the zig teeth */
	filter: ${({ theme }) =>
		theme.mode === 'dark'
			? 'drop-shadow(0 14px 22px rgba(0, 0, 0, 0.4))'
			: 'drop-shadow(0 14px 22px rgba(27, 20, 14, 0.09))'};
	transition: transform 0.3s ease;

	/* pointer devices only: the slip tilts a touch, alternating sides */
	@media (hover: hover) {
		&:hover {
			transform: rotate(0.7deg);
		}

		&:nth-child(even):hover {
			transform: rotate(-0.7deg);
		}
	}

	@media (prefers-reduced-motion: reduce) {
		transition: none;

		@media (hover: hover) {
			&:hover,
			&:nth-child(even):hover {
				transform: none;
			}
		}
	}

	.tkt {
		background: var(--panel-2);
		border-radius: 14px 14px 0 0;
		padding: 24px 22px 6px;
	}

	/* nazubljeni donji rub */
	.zig {
		height: 12px;
		background:
			linear-gradient(-45deg, transparent 8.49px, var(--panel-2) 0) 0 0 / 24px
				12px repeat-x,
			linear-gradient(45deg, transparent 8.49px, var(--panel-2) 0) 12px 0 / 24px
				12px repeat-x;
	}

	.hd {
		text-align: center;
		padding-bottom: 14px;
		border-bottom: 1px solid rgba(var(--ink-rgb), 0.25);
	}

	.hd .date {
		display: block;
		font-family: var(--fd);
		text-transform: uppercase;
		font-size: 18px;
		letter-spacing: 1px;
		color: var(--ink);
	}

	.hd .no {
		display: block;
		font-size: 11.5px;
		font-weight: 700;
		letter-spacing: 1.6px;
		text-transform: uppercase;
		color: var(--mut);
		margin-top: 4px;
	}

	/* Dot leader is an inline overflow element glued to the last word, so the
	   word→dots gap is a constant 8px on every line; the price rides the
	   item's last line and masks the leader's tail with the paper color. */
	.li {
		display: flex;
		align-items: flex-end;
		padding: 7px 0;
		font-size: 14px;
		overflow: hidden;
	}

	.li:first-of-type {
		padding-top: 14px;
	}

	.li .nm {
		flex: 1;
		min-width: 0;
		/* wrap threshold: the last line always keeps room for leader + price,
		   so the dots never end up alone on their own line */
		max-width: calc(100% - 72px);
		color: var(--ink);
		font-weight: 600;
		line-height: 1.35;
	}

	.li .nm::after {
		content: '';
		display: inline-block;
		width: 600px;
		height: 2px;
		margin-right: -600px;
		margin-left: 8px;
		vertical-align: 3px;
		border-top: 2px dotted rgba(var(--ink-rgb), 0.28);
	}

	.li .nm .by {
		color: var(--mut);
		font-weight: 500;
	}

	.li .pr {
		position: relative;
		margin-left: auto;
		background: var(--panel-2);
		padding-left: 8px;
		font-weight: 600;
		color: var(--ink);
		line-height: 1.35;
		white-space: nowrap;
	}

	.tot {
		display: flex;
		align-items: baseline;
		justify-content: space-between;
		gap: 12px;
		margin-top: 12px;
		padding: 12px 0 14px;
		border-top: 1px solid rgba(var(--ink-rgb), 0.25);
	}

	.tot .lb {
		font-size: 12px;
		font-weight: 700;
		letter-spacing: 1.8px;
		text-transform: uppercase;
		color: var(--mut);
	}

	.tot .v {
		font-family: var(--fd);
		font-size: 24px;
		color: var(--coral);
	}

	@media (max-width: 720px) {
		.tkt {
			padding: 20px 18px 4px;
		}

		.hd .date {
			font-size: 17px;
		}

		.li {
			font-size: 13.5px;
		}

		.tot .v {
			font-size: 22px;
		}
	}
`;

// ------------------------------------------------------------------ //
// UserVoucher — »ista pozornica, panorama papir« s finalom kao na 12
// (proposal 14, quizzach-voucher-panorama-proposals.html): ulaz čuva
// kompoziciju 08 (nagnuta karta, divovske obrisne riječi, centrirani
// unos), ali papir je panorama-ulaznica — sigurnosne linije, traka s
// rupicama, probušene rupe maskom, pravi barkod-kupon. Točan kod odigra
// koreografiju: ulaz izblijedi, velika poklon-kutija iskoči s prskom
// Q-ova, karta se krosfejda u ispunjenu ulaznicu (naslovnica 3:2,
// zlatna polja, kupon s akcijama) dok kutija putuje i skupi se na
// svoje mjesto uz »Tvoj kviz je stigao!«.
// ------------------------------------------------------------------ //

export const VPage = styled.div`
	text-align: left;
	min-height: 100vh;
`;

export const VoucherStage = styled.div`
	position: relative;
	min-height: calc(100vh - 90px);
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 140px var(--pad) 90px;
	overflow: hidden;

	&::before {
		content: '';
		position: absolute;
		inset: 0;
		pointer-events: none;
		opacity: 0.5;
		background: radial-gradient(
			560px 300px at 50% 42%,
			rgba(0, 255, 194, 0.06),
			transparent 70%
		);
	}
`;

export const VoucherGiant = styled.div`
	position: absolute;
	inset: 0;
	pointer-events: none;
	overflow: hidden;
	user-select: none;

	span {
		position: absolute;
		font-family: var(--fd);
		color: transparent;
		-webkit-text-stroke: 1.5px rgba(var(--ink-rgb), 0.09);
		text-transform: uppercase;
		line-height: 1;
		white-space: nowrap;
	}

	.g1 {
		font-size: clamp(90px, 13vw, 190px);
		left: -20px;
		top: 48px;
		transform: rotate(-4deg);
	}

	.g2 {
		font-size: clamp(70px, 10vw, 150px);
		right: 16px;
		bottom: 36px;
		transform: rotate(3deg);
	}

	/* mobitel: karta nosi scenu sama, obrisne riječi samo smetaju */
	@media (max-width: 720px) {
		display: none;
	}
`;

/* zajednički papirnati detalji panorama-ulaznice */
const securityLines = css`
	background-image: repeating-linear-gradient(
		45deg,
		rgba(var(--ink-rgb), 0.025) 0 1px,
		transparent 1px 8px
	);
`;

/* barkod: dvoslojni repeating-gradient, bez asseta */
const barsBg = css`
	color: var(--ink);
	background: repeating-linear-gradient(
			90deg,
			currentColor 0 2px,
			transparent 2px 5px,
			currentColor 5px 6px,
			transparent 6px 11px
		),
		repeating-linear-gradient(90deg, currentColor 0 1px, transparent 1px 7px);
`;

/* prave probušene rupe na liniji perforacije (64px kupon + pola trake) */
const sideHoles = css`
	--sh: 73px;
	-webkit-mask-image: radial-gradient(
			circle 11px at 0 calc(100% - var(--sh)),
			transparent 11px,
			#000 11.5px
		),
		radial-gradient(
			circle 11px at 100% calc(100% - var(--sh)),
			transparent 11px,
			#000 11.5px
		);
	-webkit-mask-composite: source-in;
	mask-image: radial-gradient(
			circle 11px at 0 calc(100% - var(--sh)),
			transparent 11px,
			#000 11.5px
		),
		radial-gradient(
			circle 11px at 100% calc(100% - var(--sh)),
			transparent 11px,
			#000 11.5px
		);
	mask-composite: intersect;
`;

const monoStack = css`
	font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace;
`;

/* Q-ovi lete iz kutije: domet i rotacija po spanu preko CSS varijabli */
const qfly = keyframes`
	0% { transform: translate(0, 6px) scale(0.3) rotate(0deg); opacity: 0; }
	14% { opacity: 1; }
	100% {
		transform: translate(var(--fx, 0px), var(--ry, -82px)) scale(1)
			rotate(var(--rr, 300deg));
		opacity: 0;
	}
`;

/* karta-scena: obje ulaznice naslagane u istoj grid ćeliji pa zamjena
   ne pomiče pozornicu; nagib živi ovdje — karte se same samo skaliraju,
   što mjerenje cilja za let kutije drži jednostavnim */
export const VoucherCard = styled.div<{ $busy?: boolean }>`
	position: relative;
	width: min(780px, 94%);
	display: grid;
	place-items: center;
	transform: rotate(-1.6deg);
	transition: transform 0.3s;

	> * {
		grid-area: 1 / 1;
	}

	&:hover {
		transform: ${({ $busy }) =>
			$busy ? 'rotate(-1.6deg)' : 'rotate(-0.6deg)'};
	}

	/* bez nagiba na mobitelu — dar je i dalje jasan */
	@media (max-width: 720px) {
		transform: none;

		&:hover {
			transform: none;
		}
	}
`;

/* ulazna ulaznica: glavnina guta višak visine, kupon drži svoju */
export const VoucherTicket = styled.div<{ $hide?: boolean }>`
	position: relative;
	width: 100%;
	background: var(--panel-2);
	border-radius: 18px;
	overflow: hidden;
	display: grid;
	grid-template-columns: 1fr;
	grid-template-rows: 1fr auto auto;
	min-height: 660px;
	box-shadow: 0 30px 70px rgba(0, 0, 0, 0.18);
	${sideHoles}
	transition:
		opacity 0.6s ease,
		transform 0.6s ease;

	${({ $hide }) =>
		$hide &&
		css`
			opacity: 0;
			transform: scale(0.97);
			pointer-events: none;
		`}

	@media (max-width: 720px) {
		min-height: 540px;
	}
`;

const shake = keyframes`
	0%, 100% { transform: none; }
	20%, 60% { transform: translateX(-7px); }
	40%, 80% { transform: translateX(7px); }
`;

export const VoucherTicketMain = styled.div`
	position: relative;
	padding: clamp(28px, 3.6vw, 46px);
	padding-top: clamp(36px, 5vw, 64px);
	display: flex;
	flex-direction: column;
	align-items: center;
	text-align: center;
	${securityLines}

	h2 {
		font-size: clamp(30px, 3.4vw, 48px);
		margin-top: 2px;
	}

	p {
		color: var(--brown);
		max-width: 440px;
		margin-top: 12px;
	}

	form {
		display: flex;
		flex-direction: column;
		gap: 16px;
		margin-top: 26px;
		width: 100%;
		max-width: 440px;

		&.shake {
			animation: ${shake} 0.4s;
		}
	}

	input {
		box-sizing: border-box;
		width: 100%;
		background: var(--panel-2);
		border: 1.5px solid rgba(var(--ink-rgb), 0.3);
		border-radius: var(--r-pill);
		padding: 14px 24px;
		font-family: var(--fd);
		font-size: 20px;
		letter-spacing: 3px;
		text-transform: uppercase;
		text-align: center;
		color: var(--ink);
		outline: none;
		transition: border-color 0.2s;

		&:focus {
			border-color: var(--teal-dark);
		}

		&.bad {
			border-color: var(--coral);
		}

		&::placeholder {
			font-family: var(--fb);
			font-size: 16.5px;
			font-weight: 500;
			letter-spacing: normal;
			text-transform: none;
			color: var(--mut);
		}
	}

	/* mekani coral tint — alert jezik dizajn-sustava */
	.terr {
		box-sizing: border-box;
		display: flex;
		align-items: center;
		gap: 12px;
		margin-top: 16px;
		width: 100%;
		max-width: 440px;
		padding: 13px 20px;
		border-radius: 27px;
		background: ${({ theme }) =>
			theme.mode === 'dark'
				? 'rgba(255, 126, 87, 0.13)'
				: 'rgba(241, 107, 70, 0.11)'};
		color: var(--ink);
		font-size: 14.5px;
		font-weight: 600;
		line-height: 1.45;
		text-align: left;

		svg {
			color: var(--coral);
			flex-shrink: 0;
		}
	}
`;

/* perforacija: točkice u boji stranice na papirnatoj traci */
export const VoucherHoles = styled.div`
	height: 18px;
	background-color: var(--panel-2);
	background-image: radial-gradient(
		circle 4px at 8px 9px,
		var(--cream) 4px,
		transparent 4.5px
	);
	background-size: 16px 18px;
	background-repeat: repeat-x;
`;

/* dekorativni kupon ulaznog stanja: naljepnica + barkod koji čeka kod */
export const VoucherFoot = styled.div`
	min-height: 64px;
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 14px;
	flex-wrap: wrap;
	padding: 12px 22px;
	${securityLines}

	.flab {
		font-family: var(--fd);
		font-size: 12px;
		letter-spacing: 3px;
		text-transform: uppercase;
		color: var(--mut);
	}

	.bars {
		height: 24px;
		width: 110px;
		opacity: 0.75;
		${barsBg}
	}
`;

/* ispunjena ulaznica — ista karta koju kolekcija pokazuje, uža od ulazne */
export const VoucherWon = styled.div<{ $show?: boolean; $done?: boolean }>`
	position: relative;
	width: min(480px, 100%);
	border-radius: 18px;
	overflow: hidden;
	background: var(--panel-2);
	box-shadow: 0 30px 70px rgba(0, 0, 0, 0.18);
	${sideHoles}
	opacity: 0;
	transform: scale(0.96);
	pointer-events: none;
	transition:
		opacity 0.6s ease,
		transform 0.6s ease;

	${({ $show }) =>
		$show &&
		css`
			opacity: 1;
			transform: none;
			pointer-events: auto;
		`}

	/* naslovnica u punom 3:2, darkzone samo pod potpisom */
	.ph {
		position: relative;
		aspect-ratio: 3 / 2;
		overflow: hidden;
		background: #17130f;
	}

	.ph img.bg {
		position: absolute;
		inset: 0;
		width: 100%;
		height: 100%;
		object-fit: cover;
	}

	.ph::after {
		content: '';
		position: absolute;
		inset: 0 0 55% 0;
		background: linear-gradient(
			180deg,
			rgba(10, 8, 6, 0.45),
			rgba(10, 8, 6, 0)
		);
	}

	/* autorski potpis u brand boji: wordmark ili Q ✕ autorov neon logo */
	.amark {
		position: absolute;
		top: 14px;
		left: 16px;
		z-index: 1;
		display: inline-flex;
		align-items: center;
		gap: 7px;
		color: var(--teal);
		pointer-events: none;
	}

	.amark svg {
		display: block;
		flex-shrink: 0;
	}

	.amark .author-img img {
		display: block;
		height: 22px;
		width: auto;
	}

	.amark .author-img .v-base {
		display: none;
	}

	.amark .author-img .v-neon {
		display: block;
	}

	.amark .author-hand {
		font-family: var(--fh);
		font-weight: 600;
		font-size: 23px;
		line-height: 1;
		color: var(--teal);
		white-space: nowrap;
	}

	/* glavnina mirna — sigurnosne linije ostaju samo na kuponu */
	.main {
		position: relative;
		padding: 22px 26px 20px;
	}

	/* dar-ikona sama, centrirana */
	.ok {
		display: flex;
		align-items: center;
		justify-content: center;
	}

	/* inline dar-ikona čeka da joj velika kutija sleti, pa preuzme */
	.ok .gift-pop {
		position: relative;
		display: inline-flex;
		color: var(--teal-dark);
		opacity: ${({ $done }) => ($done ? 1 : 0)};
		transition: opacity 0.2s;
	}

	.gift-pop .qf {
		position: absolute;
		left: 50%;
		top: -4px;
		margin-left: -8px;
		pointer-events: none;
		animation: ${qfly} 2.4s ease-out infinite;
	}

	.gift-pop .qf svg {
		display: block;
	}

	/* naslov + broj pitanja kao na profilnoj karti u mreži */
	.tt {
		margin-top: 14px;
		text-align: left;
	}

	.tt h3 {
		font-size: 21px;
		line-height: 1.12;
	}

	.tt .im {
		display: block;
		margin-top: 6px;
		font-size: 10px;
		font-weight: 700;
		letter-spacing: 1.8px;
		text-transform: uppercase;
		color: var(--mut);
	}

	/* kupon: barkod + ID lijevo, akcije desno */
	.stub {
		min-height: 64px;
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 12px;
		flex-wrap: wrap;
		padding: 12px 18px;
		${securityLines}
	}

	/* the code text sizes the column; the bars stretch to match it */
	.stub .bc {
		display: flex;
		flex-direction: column;
		gap: 4px;
	}

	.stub .bars {
		height: 24px;
		${barsBg}
	}

	.stub .code {
		${monoStack}
		font-size: 8.5px;
		letter-spacing: 2.2px;
		text-transform: uppercase;
		color: var(--mut);
		white-space: nowrap;
	}

	.acts {
		display: flex;
		gap: 10px;
		flex-wrap: wrap;
		justify-content: flex-end;
	}

	/* mobitel: samo igraj, u istom redu s barkodom (kao profilna karta);
	   kolekcija je ionako korak dalje — donja navigacija vodi na profil */
	@media (max-width: 720px) {
		.stub {
			flex-wrap: nowrap;
		}

		.stub .bc {
			min-width: 0;
		}

		.acts .collect {
			display: none;
		}
	}
`;

/* velika poklon-kutija: iskoči na sredinu pozornice, pa se na reveal
   skupi (scale 1 → 0.345 = 110px → 38px inline ikona) i klizi na cilj
   izmjeren iz DOM-a (--tx/--ty postavlja komponenta) */
export const VoucherBigGift = styled.div<{
	$phase: 'entry' | 'go' | 'reveal' | 'done';
}>`
	position: absolute;
	left: 50%;
	top: 50%;
	z-index: 6;
	color: var(--teal-dark);
	opacity: 0;
	pointer-events: none;
	transform: translate(-50%, -50%) scale(0);

	svg.box {
		display: block;
	}

	.qf {
		position: absolute;
		left: 50%;
		top: -8px;
		margin-left: -11px;
		opacity: 0;
		pointer-events: none;
	}

	.qf svg {
		display: block;
	}

	${({ $phase }) =>
		$phase === 'go' &&
		css`
			opacity: 1;
			transform: translate(-50%, -50%) scale(1);
			transition:
				transform 0.55s cubic-bezier(0.2, 0.9, 0.3, 1.35),
				opacity 0.2s;
		`}

	${({ $phase }) =>
		($phase === 'go' || $phase === 'reveal') &&
		css`
			.qf {
				animation: ${qfly} 1.5s ease-out infinite;
			}
		`}

	/* done zadržava transformaciju slijetanja — faze se ne zbrajaju kao
	   klase u mocku, pa bi povratak na bazni scale(0) vidljivo trznuo
	   kutiju natrag na sredinu dok blijedi */
	${({ $phase }) =>
		($phase === 'reveal' || $phase === 'done') &&
		css`
			transform: translate(
					calc(-50% + var(--tx, 0px)),
					calc(-50% + var(--ty, 0px))
				)
				scale(0.345);
		`}

	${({ $phase }) =>
		$phase === 'reveal' &&
		css`
			opacity: 1;
			transition: transform 1.3s cubic-bezier(0.45, 0, 0.25, 1) 0.1s;
		`}

	${({ $phase }) =>
		$phase === 'done' &&
		css`
			opacity: 0;
			transition: opacity 0.2s;
		`}
`;

const cfall = keyframes`
	0% { transform: translate(0, 0) rotate(0); opacity: 1; }
	100% { transform: translate(var(--dx, 20px), 440px) rotate(320deg); opacity: 0; }
`;

export const VoucherCf = styled.i`
	position: absolute;
	top: 6%;
	border-radius: 2px;
	pointer-events: none;
	z-index: 4;
	animation: ${cfall} 2.4s ease-out forwards;
`;

export const VoucherCfq = styled.span`
	position: absolute;
	top: 4%;
	pointer-events: none;
	z-index: 4;
	animation: ${cfall} 2.7s ease-out forwards;

	svg {
		display: block;
		width: 100%;
		height: 100%;
	}
`;
